<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from '@/i18n'

const props = defineProps<{
  currentPage: number
  totalPages: number
  total: number
  perPage: number
}>()

const emit = defineEmits<{
  (e: 'page-change', page: number): void
}>()
const { t } = useI18n()

// แสดง page numbers รอบๆ current page
const visiblePages = computed(() => {
  const pages: (number | string)[] = []
  const current = props.currentPage
  const total = props.totalPages

  if (total <= 7) {
    // แสดงทุกหน้า
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    // แสดงแบบ 1 ... 4 5 6 ... 10
    pages.push(1)

    if (current > 3) {
      pages.push('...')
    }

    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      if (!pages.includes(i)) {
        pages.push(i)
      }
    }

    if (current < total - 2) {
      pages.push('...')
    }

    if (!pages.includes(total)) {
      pages.push(total)
    }
  }

  return pages
})

// const startItem = computed(() => (props.currentPage - 1) * props.perPage + 1)
// const endItem = computed(() => Math.min(props.currentPage * props.perPage, props.total))
</script>

<template>
  <nav v-if="totalPages > 1" class="flex flex-col items-center space-y-3 mt-6">
    <!-- Pagination Controls -->
    <ul class="flex items-center space-x-1">
      <!-- First -->
      <li>
        <button
          @click="emit('page-change', 1)"
          :disabled="currentPage === 1"
          class="px-3 py-2 rounded-md text-sm font-medium transition-colors"
          :class="currentPage === 1
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100'"
          :aria-label="t('First page')"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
      </li>

      <!-- Previous -->
      <li>
        <button
          @click="emit('page-change', currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-3 py-2 rounded-md text-sm font-medium transition-colors"
          :class="currentPage === 1
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100'"
          :aria-label="t('Previous page')"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </li>

      <!-- Page Numbers -->
      <li v-for="(pageNum, index) in visiblePages" :key="index">
        <span
          v-if="pageNum === '...'"
          class="px-3 py-2 text-gray-400"
        >...</span>
        <button
          v-else
          @click="emit('page-change', pageNum as number)"
          class="px-3 py-2 rounded-md text-sm font-medium transition-colors min-w-[40px]"
          :class="currentPage === pageNum
            ? 'bg-blue-500 text-white'
            : 'text-gray-600 hover:bg-gray-100'"
        >
          {{ pageNum }}
        </button>
      </li>

      <!-- Next -->
      <li>
        <button
          @click="emit('page-change', currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="px-3 py-2 rounded-md text-sm font-medium transition-colors"
          :class="currentPage === totalPages
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100'"
          :aria-label="t('Next page')"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </li>

      <!-- Last -->
      <li>
        <button
          @click="emit('page-change', totalPages)"
          :disabled="currentPage === totalPages"
          class="px-3 py-2 rounded-md text-sm font-medium transition-colors"
          :class="currentPage === totalPages
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100'"
          :aria-label="t('Last page')"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>
      </li>
    </ul>

    <!-- Page Info -->
    <p class="text-sm text-gray-500">
      {{ t('Page {page} of {pages} ({total} stations total)', { page: currentPage, pages: totalPages, total }) }}
    </p>
  </nav>
</template>
